import { useState } from 'react'
import { CAMADA_DA_CHAMADA } from './camadas.ts'
import { AnchoredOverlay } from '@orbind/ui'
import { MenuDeUmAparelho, TesteDoMicrofone, TesteDoSom } from './controles-de-midia.tsx'
import { aplicarSaida, useAparelhos, type TipoDeAparelho } from './dispositivos.ts'

/**
 * A setinha ao lado do botão de cada aparelho (SPEC-20260918-0040).
 *
 * O desenho é do usuário (2026-09-18 16:15) e é melhor que o anterior: a barra
 * volta a ser só botões, sem o nome do aparelho ocupando espaço permanente. A
 * seta abre um cartão CURTO logo acima, que diz qual aparelho está em uso — e
 * só quem quer trocar clica nos três pontos para ver a lista inteira.
 *
 * Microfone e saída de som vêm JUNTOS na mesma seta: quem abre "o som" quer
 * resolver o áudio, e áudio são as duas pontas. Separá-los obrigava a procurar
 * em dois lugares o que é uma decisão só.
 */

type Linha = { tipo: TipoDeAparelho; titulo: string; icone: string }

export function SeletorDeAparelho({
  linhas,
  rotulo,
  faixaDoMicrofone,
  micLigado,
  onTrocarMicrofone,
  onTrocarCamera,
  elementoDeSaida,
}: {
  /** Quais aparelhos este seletor cobre — o do microfone cobre dois. */
  linhas: Linha[]
  rotulo: string
  faixaDoMicrofone?: MediaStreamTrack | null
  micLigado?: boolean
  onTrocarMicrofone?: (id: string) => void
  onTrocarCamera?: (id: string) => void
  elementoDeSaida?: HTMLMediaElement | null
}) {
  const [cartao, setCartao] = useState<HTMLElement | null>(null)
  const [lista, setLista] = useState<{ ancora: HTMLElement; tipo: TipoDeAparelho } | null>(null)

  const aoTrocar = (tipo: TipoDeAparelho) => (id: string) => {
    if (tipo === 'microfone') onTrocarMicrofone?.(id)
    else if (tipo === 'camera') onTrocarCamera?.(id)
    else void aplicarSaida(elementoDeSaida ?? null)
  }

  const rodapeDe = (tipo: TipoDeAparelho) => {
    if (tipo === 'microfone') return <TesteDoMicrofone faixa={faixaDoMicrofone ?? null} ativo={micLigado ?? false} />
    if (tipo === 'saida') return <TesteDoSom />
    return undefined
  }

  return (
    <>
      <button
        type="button"
        className="vc-seta"
        aria-haspopup="dialog"
        aria-expanded={cartao !== null}
        aria-label={rotulo}
        title={rotulo}
        onClick={(e) => {
          const alvo = e.currentTarget
          setCartao((aberto) => (aberto ? null : alvo))
        }}
      >
        <i className="fas fa-chevron-up" aria-hidden />
      </button>

      {cartao ? (
        <AnchoredOverlay
          anchor={cartao}
          onClose={() => {
            setCartao(null)
            setLista(null)
          }}
          prefer="acima"
          gap={12}
          role="dialog"
          aria-label={rotulo}
          className="vc-cartao"
          zIndex={CAMADA_DA_CHAMADA}
        >
          {linhas.map((l) => (
            <LinhaDoAparelho
              key={l.tipo}
              linha={l}
              onAbrirLista={(ancora) => setLista({ ancora, tipo: l.tipo })}
            />
          ))}
        </AnchoredOverlay>
      ) : null}

      {lista ? (
        <MenuDeUmAparelho
          ancora={lista.ancora}
          titulo={linhas.find((l) => l.tipo === lista.tipo)?.titulo ?? ''}
          icone={linhas.find((l) => l.tipo === lista.tipo)?.icone ?? ''}
          tipo={lista.tipo}
          onFechar={() => setLista(null)}
          onTrocar={aoTrocar(lista.tipo)}
          rodape={rodapeDe(lista.tipo)}
        />
      ) : null}
    </>
  )
}

/** Uma linha do cartão: ícone, nome do que está em uso, e os três pontos. */
function LinhaDoAparelho({
  linha,
  onAbrirLista,
}: {
  linha: Linha
  onAbrirLista: (ancora: HTMLElement) => void
}) {
  const aparelhos = useAparelhos(linha.tipo)
  const atual = aparelhos.lista.find((a) => a.id === aparelhos.escolhido) ?? aparelhos.lista[0]

  return (
    <div className="vc-cartao__linha">
      <i className={`${linha.icone} vc-cartao__icone`} aria-hidden />
      <span className="vc-cartao__nome" title={atual?.nome ?? linha.titulo}>
        {atual?.nome ?? 'Padrão do sistema'}
      </span>
      <button
        type="button"
        className="vc-cartao__mais"
        aria-label={`Trocar ${linha.titulo.toLowerCase()}`}
        title={`Trocar ${linha.titulo.toLowerCase()}`}
        onClick={(e) => onAbrirLista(e.currentTarget)}
      >
        <i className="fas fa-ellipsis-h" aria-hidden />
      </button>
    </div>
  )
}
